import qs from 'query-string'
import getRequest from 'utils/getRequest'
import postRequest from 'utils/postRequest'
import uploadFileRequest from 'utils/uploadFileRequest'
import deleteRequest from 'utils/deleteRequest'
import filterUndefinedFromObject, { checkIfIntegrityVerified } from 'utils/filterUndefinedFromObject'
import patchRequest from 'utils/patchRequest'
import xhrFormDataRequest from 'utils/xhrFormDataRequest'
import fetchJsonRequest from 'utils/fetchJsonRequest'
import { editOnly } from 'routing/constants'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'

const api = `/api`
const path = `${api}/site`
const csvPath = `${api}/csv`
const reassessmentPath = `${api}/reassessment/sites`
const siteSpeciePath = `${api}/site-specie`
const criterionCPath = `${api}/criterion-c`
const commentsPath = `${api}/comments`
const repliesPath = `${api}/replies`
const visibilityPath = `${api}/user/site-permissions/site`
const editSitePath = `${api}/editSite/sites`

const getCommentsBasePath = ({ pathValue, siteId, siteHistoryId }) => {
  if (pathValue === editOnly) {
    return `${editSitePath}/${siteId}`
  }

  if (siteHistoryId) {
    return `${reassessmentPath}/${siteHistoryId}`
  }

  return null
}

const buildCommentUrl = ({ siteId, siteHistoryId, commentId, pathValue }) => {
  const basePath = getCommentsBasePath({ pathValue, siteId, siteHistoryId })

  if (basePath) {
    return `${basePath}/comments/${commentId}`
  }

  return `${commentsPath}/${commentId}/site/${siteId}`
}

const buildCommentsCollectionUrl = ({ siteId, siteHistoryId, pathValue }) => {
  const basePath = getCommentsBasePath({ pathValue, siteId, siteHistoryId })
  return basePath ? `${basePath}/comments` : `${commentsPath}/site/${siteId}`
}

const buildReplyUrl = ({ siteId, siteHistoryId, replyId, pathValue }) => {
  const basePath = getCommentsBasePath({ pathValue, siteId, siteHistoryId })
  if (basePath) {
    return `${basePath}/replies/${replyId}`
  }
  return `${repliesPath}/${replyId}/site/${siteId}`
}

const buildCommentRepliesUrl = ({ siteId, siteHistoryId, commentId, pathValue }) => {
  const basePath = getCommentsBasePath({ pathValue, siteId, siteHistoryId })
  if (basePath) {
    return `${basePath}/comments/${commentId}/replies`
  }
  return `${repliesPath}/site/${siteId}/comments/${commentId}`
}

const buildCommentActionUrl = ({ siteId, siteHistoryId, commentId, pathValue, action }) => {
  const basePath = getCommentsBasePath({ pathValue, siteId, siteHistoryId })
  if (basePath) {
    return `${basePath}/comments/${commentId}/${action}`
  }
  return `${commentsPath}/${commentId}/site/${siteId}/${action}`
}

export const requestGetSites = ({
  resultsPerPage = 12,
  currentPage = 1,
  siteStatus = '',
  year = '',
  country = '',
  sortBy = 'name',
  sortDirection = 'ASC',
  toggle = false,
  signal
}) => {
  const queryString = {
    resultsPerPage,
    currentPage,
    siteStatus,
    year,
    country,
    sortBy,
    toggle,
    sortDirection
  }
  const queryStringParsed = qs.stringify(queryString)
  return getRequest(`${path}/?${queryStringParsed}`, signal)
}

export const requestProposeSite = () => {
  return postRequest(`${path}/`)
}

export const deleteSiteRequest = (siteId, reassessmentFlag, siteHistoryId) => {
  const url = reassessmentFlag ? `${reassessmentPath}/${siteHistoryId}/delete` : `${path}/${siteId}/supersed`
  // Reassessment delete validates SiteStatusId; send draft status (middleware also verifies DB state).
  return reassessmentFlag
    ? deleteRequest(url, { SiteStatusId: SITE_STATUS_DRAFT_ID })
    : deleteRequest(url)
}

export const deleteEditSiteOnlyRequest = (editOnlySiteId, SiteStatusId) => {
  const url = `${editSitePath}/${editOnlySiteId}/delete`
  return deleteRequest(url, { SiteStatusId })
}

export const requestReassesSite = (siteId) => {
  return postRequest(`${reassessmentPath}/${siteId}`)
}

export const requestGetSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}` : `${path}/${siteId}`
  return getRequest(url)
}

export const requestGetEditOnlySite = async (editOnlySiteId) => {
  return await getRequest(`${editSitePath}/${editOnlySiteId}`)
}

/**
 * Creates or replaces an existing draft KBA geometry
 * @param {Number} siteId - the site id for the draft KBA
 * @param {Number} siteHistoryId - the site history id for a site being reassessed
 * @param {object} geometry - the polygon geometry of the draft KBA
 * @param {object} attributes - the attributes to add to the feature on the draft layer
 * @returns {Promise}
 */
export const postDraftSiteGeometry = (siteId, siteHistoryId, geometry, attributes) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/geometry` : `${path}/${siteId}/geometry`
  return postRequest(url, { geometry, attributes })
}

/**
 * Deletes the site geometry for an existing draft KBA
 * @param {string} siteId
 * @param {Number} siteHistoryId - the site history id for a site being reassessed
 * @returns {Promise}
 */
export const deleteDraftSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/delete/geometry` : `${path}/${siteId}/delete/geometry`
  return deleteRequest(url)
}

/**
 * Gets the geometry for a current KBA
 * @param {string} siteId
 * @returns {Promise}
 */
export const requestGetCurrentSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/geometry` : `${path}/${siteId}/geometry`
  return getRequest(url)
}

/**
 * Gets the geometry for a draft KBA
 * @param {string} siteId
 * @returns {Promise}
 */
export const requestGetDraftSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/draftGeometry` : `${path}/${siteId}/draftGeometry`
  return getRequest(url)
}

export const requestGetSitesSearch = ({
  resultsPerPage = 12,
  currentPage = 1,
  siteStatus = '',
  year = '',
  country = '',
  sortBy = 'name',
  query = '',
  sortDirection = 'ASC',
  toggle = false,
  signal
}) => {
  const queryString = {
    resultsPerPage,
    currentPage,
    siteStatus,
    year,
    country,
    sortBy,
    toggle,
    sortDirection,
    query
  }

  const queryStringParsed = qs.stringify(queryString)
  return getRequest(`${path}/search?${queryStringParsed}`, signal)
}

export const requestGetPotentialCoProposers = ({ searchQuery = '', excludeIds = [] }, signal) => {
  return postRequest(`${path}/co-proposer`, { searchQuery, excludeIds }, signal)
}

export const requestRemoveCoProposerFromSite = ({ siteId, proposerId }, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/co-proposers/${proposerId}`
    : `${path}/${siteId}/co-proposer/${proposerId}`
  return deleteRequest(url)
}

export const requestGetSiteCoProposers = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/co-proposers` : `${path}/${siteId}/co-proposer`
  return getRequest(url)
}

export const requestAddNewCoProposer = ({ siteId, newProposers }, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/co-proposers` : `${path}/${siteId}/co-proposer`
  return postRequest(url, { newProposers })
}

export const requestAllSitesReassessments = (siteId) => {
  return getRequest(`${path}/${siteId}/reassessments`)
}

export const requestCalculationData = (
  siteId,
  siteHistoryId,
  {
    // eslint-disable-next-line no-magic-numbers
    resultsPerPage = 8,
    // eslint-disable-next-line no-magic-numbers
    currentPage = 1,
    search,
    taxonomicGroup,
    criteriaStatus,
    underAssessment,
    sortBy,
    sortDirection
  } = {}
) => {
  const queryString = {
    resultsPerPage,
    currentPage
  }

  if (search) {
    queryString.search = search
  }
  if (taxonomicGroup) {
    queryString.taxonomicGroup = taxonomicGroup
  }
  if (criteriaStatus) {
    queryString.criteriaStatus = criteriaStatus
  }
  if (underAssessment) {
    queryString.underAssessment = underAssessment
  }
  if (sortBy) {
    queryString.sortBy = sortBy
    queryString.sortDirection = sortDirection
  }

  const queryStringParsed = qs.stringify(queryString)
  if (siteHistoryId) {
    return getRequest(`${reassessmentPath}/${siteHistoryId}/kba-criterias?${queryStringParsed}`)
  }
  return getRequest(`${path}/${siteId}/kba-criterias?${queryStringParsed}`)
}

export const requestEcoSystemCalculator = (siteId, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/eco-systems/Kba-criterias`
    : `/api/eco-system/site/${siteId}/kba-criterias`

  return getRequest(url)
}

export const requestTreatmentData = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/treatment` : `${path}/${siteId}/treatment`
  return getRequest(url)
}

// requests for comments section

export const removeCommentById = async(siteId, commentId, siteHistoryId, path = null) => {
  const url = buildCommentUrl({ siteId, siteHistoryId, commentId, pathValue: path })
  return deleteRequest(url)
}

export const requestCommentsData = async (siteId, siteHistoryId, path=null) => {
  const url = buildCommentsCollectionUrl({ siteId, siteHistoryId, pathValue: path })
  return await getRequest(url)
}

export const requestAddCommentData = async (siteId, siteHistoryId, data, path=null) => {
  const url = buildCommentsCollectionUrl({ siteId, siteHistoryId, pathValue: path })
  return await postRequest(url, data)
}

export const updateCommentById = (siteId, commentId, siteHistoryId, data, path = null) => {
  const url = buildCommentUrl({ siteId, siteHistoryId, commentId, pathValue: path })
  return patchRequest(url, data)
}

// requests for replies section

export const updateReplyById = (siteId, replyId, siteHistoryId, data, path = null) => {
  const url = buildReplyUrl({ siteId, siteHistoryId, replyId, pathValue: path })
  return patchRequest(url, data)
}

export const requestAddReplyData = async (siteId, commentId, siteHistoryId, data, path = null) => {
  const url = buildCommentRepliesUrl({ siteId, siteHistoryId, commentId, pathValue: path })
  return await postRequest(url, data)
}

export const requestAddSubReplyData = (siteId, commentId, data) => {
  return postRequest(`${repliesPath}/site/${siteId}/comments/${commentId}`, data)
} // not yet used

export const requestGetReplyCopy = async (siteId, commentId, siteHistoryId, data, path=null) => {
  const url = buildCommentRepliesUrl({ siteId, siteHistoryId, commentId, pathValue: path })
  return await getRequest(url, data)
}

export const removeReplyCopy2 = async(siteId, replyId, siteHistoryId, data, path=null) => {
  const url = buildReplyUrl({ siteId, siteHistoryId, replyId, pathValue: path })
  return deleteRequest(url, data)
}

export const resolveCommentById = async (siteId, commentId, siteHistoryId, path = null) => {
  const url = buildCommentActionUrl({ siteId, siteHistoryId, commentId, pathValue: path, action: 'resolve' });
  return await patchRequest(url);
};

export const reactivateCommentById = (siteId, commentId, siteHistoryId, path=null) => {
  const url = buildCommentActionUrl({ siteId, siteHistoryId, commentId, pathValue: path, action: 'reactivate' });
  return patchRequest(url);
};

// Uses fetch directly (not postRequest) because postRequest auto-follows redirect
// responses, which can cause full-page reloads. Returns plain object with
// alreadySubmitted / already_submitted / message for callers.
const shouldMockSensitiveSpeciesAck = () =>
  globalThis.window !== undefined && globalThis.sessionStorage?.getItem('mockSensitiveSpeciesError') === '1'

export const requestSubmitSensitiveSpeciesAcknowledgment = async (siteId, siteHistoryId, taxonId = null) => {
  if (shouldMockSensitiveSpeciesAck()) {
    return {}
  }
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/sensitive-species-acknowledgment`
    : `${path}/${siteId}/sensitive-species-acknowledgment`

  try {
    const { data } = await fetchJsonRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ taxonId }),
      fallback: {}
    })
    return data
  } catch {
    return {}
  }
}
export const requestUpdateSiteFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}` : `${path}/${siteId}`
  return xhrFormDataRequest({
    url,
    method: siteHistoryId ? 'PATCH' : 'POST',
    formData,
    onProgress: options.onProgress
  })
}

export const requestUploadAboutPageDocs = async (siteId, formData) => {
  const { data } = await fetchJsonRequest(`/api/pages/about/${siteId}/upload-about-docs`, {
    method: 'POST',
    body: formData,
    fallback: {}
  })
  return data
}


export const requestUploadSpeciesDocs = async (siteId, formData) => {
  const { data } = await fetchJsonRequest(`${siteSpeciePath}/${siteId}/upload-species-docs`, {
    method: 'POST',
    body: formData,
    fallback: {}
  })
  return data
}


export const requestDeleteSiteFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/deleteSiteFiles/${fileId}` : `${path}/${siteId}/deleteSiteFiles/${fileId}`
  const { data } = await fetchJsonRequest(url, {
    method: 'DELETE',
    fallback: {}
  })
  return data

}

export const requestUpdateSite = async(siteId, siteHistoryId, {
  name,
  threatenedBiodiversity,
  geographicallyRestrictedBiodiversity,
  ecologicalIntegrity,
  biologicalProcesses,
  irreplaceabilityThroughQuantitiveAnalysis,
  reviewersContactsThatCanComment,
  ongoingConservationActions,
  conservationActionsNeeded,
  siteNameNational,
  state,
  siteArea,
  siteAreaCalculated,
  latitudeOfMidPoint,
  longitudeOfMidPoint,
  lowestAltitude,
  highestAltitude,
  rationaleForSiteInformation,
  siteDescription,
  howIsTheSiteManaged,
  deliniationRationale,
  boundary_source,
  coveredByWhatProtectedAreas,
  additionalBiodiversityValues,
  indigenousGroups,
  landUseRegimesAtSite,
  notes,
  references,
  calculatedProtectedArea,
  pa_user_updated,
  pa_user_updated_justification,
  CoverageOfProtectedAreasId,
  AltitudeOfBathymetryId,
  EcoSystemsId,
  EcoSystems2LargestId,
  EcoSystems3LargestId,
  EcoSystemsSmallestId,
  terrestrial_system,
  marine_system,
  freshwater_system,
  subterranean_system,
  SiteDetailsCountryId,
  EcologicalIntegrityEcoRegionId,
  numberOfCriterionSitesInEcoRegion,
  evidenceOfLowHumanImpact,
  evidenceForIntactEcologicalCommunities,
  dateOfAssesment,
  assessmentDate,
  overlapOECMProtectedCoverage,
  proposalPurpose,
  proposalDetails,
  consultationWithBiodiversityKnowledgeHolders,
  consultationWithPreviousProposers,
  consultationWithGovernment,
  consultationWithCustomaryLegalRightsHolders,
  consultationWithContributors,
  triggerReasonId,
  triggerReasonText,
  threatsDescription,
  minDepth,
  maxDepth,
  supersededSitesDesription,
  calculatedOECMArea,
  oecm_user_updated,
  oecm_user_updated_justification,
  habitatDescription,
  irreplaceabilityScore,
  irreplaceabilityAssessmentApproved,
  contributors_assessment,
  sensitive_site,
  sensitive_site_details,
}) => {

  const preBody = {
    name,
    threatenedBiodiversity,
    geographicallyRestrictedBiodiversity,
    ecologicalIntegrity,
    biologicalProcesses,
    irreplaceabilityThroughQuantitiveAnalysis,
    reviewersContactsThatCanComment,
    ongoingConservationActions,
    conservationActionsNeeded,
    siteNameNational,
    state,
    siteArea,
    siteAreaCalculated,
    latitudeOfMidPoint,
    longitudeOfMidPoint,
    lowestAltitude,
    highestAltitude,
    rationaleForSiteInformation,
    siteDescription,
    howIsTheSiteManaged,
    deliniationRationale,
    boundary_source,
    coveredByWhatProtectedAreas,
    additionalBiodiversityValues,
    indigenousGroups,
    landUseRegimesAtSite,
    notes,
    references,
    calculatedProtectedArea,
    pa_user_updated,
    pa_user_updated_justification,
    CoverageOfProtectedAreasId,
    AltitudeOfBathymetryId,
    EcoSystemsId,
    EcoSystems2LargestId,
    EcoSystems3LargestId,
    EcoSystemsSmallestId,
    terrestrial_system,
    marine_system,
    freshwater_system,
    subterranean_system,
    SiteDetailsCountryId,
    EcologicalIntegrityEcoRegionId,
    numberOfCriterionSitesInEcoRegion,
    evidenceOfLowHumanImpact,
    evidenceForIntactEcologicalCommunities,
    dateOfAssesment,
    assessmentDate,
    overlapOECMProtectedCoverage,
    proposalPurpose,
    proposalDetails,
    consultationWithBiodiversityKnowledgeHolders,
    consultationWithPreviousProposers,
    consultationWithGovernment,
    consultationWithCustomaryLegalRightsHolders,
    consultationWithContributors,
    triggerReasonId,
    triggerReasonText,
    threatsDescription,
    minDepth,
    maxDepth,
    supersededSitesDesription,
    calculatedOECMArea,
    oecm_user_updated,
    oecm_user_updated_justification,
    habitatDescription,
    irreplaceabilityScore,
    irreplaceabilityAssessmentApproved,
    contributors_assessment,
    sensitive_site,
    sensitive_site_details,
  }
  const requestBody = filterUndefinedFromObject(preBody)


  const isIntegrityC = checkIfIntegrityVerified(requestBody)
  if (isIntegrityC) {
    requestBody.ecologicalIntegrity = true
  } else {
    requestBody.ecologicalIntegrity = false
  }
  if (siteHistoryId) {
    return await patchRequest(`${reassessmentPath}/${siteHistoryId}`, requestBody)
  }
  return await postRequest(`${path}/${siteId}`, requestBody)
}

export const requestUploadGISFile = (siteId, formDataWithFile) => {
  return uploadFileRequest(`${path}/${siteId}/upload-gis-file`, formDataWithFile)
}

export const requestFinishProposeSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/finish-propose` : `${path}/${siteId}/finish-propose`
  return postRequest(url)
}

export const requestValidateSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/validate` : `${path}/${siteId}/validate`
  return postRequest(url)
}

export const requestValidateEditOnlySite = (siteHistoryId) => {
  const url =  `${editSitePath}/${siteHistoryId}/validate` 
  return postRequest(url)
}

export const requestValidateSiteGeometry = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/validateGeometry` : `${path}/${siteId}/validateGeometry`
  return postRequest(url)
}

export const requestSupersedSite = (siteId, supersedComment) => {
  return postRequest(`${path}/${siteId}/supersed`, { supersedComment })
}

export const requestUndoSupersedSite = (siteId) => {
  return postRequest(`${path}/${siteId}/undo-supersed`)
}

export const requestReviewByRfp = (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/review-by-rfp` : `${path}/${siteId}/review-by-rfp`

  return postRequest(url, isApproved)
}

export const requestNominateSite = (siteId, siteHistoryId) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/nominate` : `${path}/${siteId}/nominate`

  return postRequest(url)
}

export const requestSiteProposalPermissions = (siteId, siteHistoryId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/proposal-permissions`
    : `${path}/${siteId}/proposal-permissions`

  return getRequest(url)
}

export const requestConfirmState = async (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId ? `${reassessmentPath}/${siteHistoryId}/confirm` : `${path}/${siteId}/confirm`

  return await postRequest(url, isApproved)
}

/**
 * Approve/reject submitted edit-only changes.
 * BE route is `/:editOnlySiteId/approve-edits/:id` — first segment must be the SitesEditOnly PK.
 * Second segment is unused by the controller; pass parent site id for URL completeness.
 */
export const requestApproveEdits = async (editOnlySiteId, parentSiteId, isApproved) => {
  const secondSegment = parentSiteId || editOnlySiteId
  const url = `${editSitePath}/${editOnlySiteId}/approve-edits/${secondSegment}`
  return await postRequest(url, isApproved)
}

export const requestDelistState = (siteHistoryId, isApproved) => {
  return postRequest(`${reassessmentPath}/${siteHistoryId}/delist`, { isApproved: isApproved })
}

export const requestDoesNotQualify = (siteId) => {
  return postRequest(`${path}/${siteId}/does-not-qualify`)
}

export const requestSiteVisibility = (siteId) => {
  return getRequest(`${visibilityPath}/${siteId}`)
}

export const requestValidateNewImportData = (data) => {
  return uploadFileRequest(`${csvPath}/upload/validate`, data)
}

export const requestImportNewData = (data) => {
  return uploadFileRequest(`${csvPath}/upload/update`, data)
}
export const requestBulkImportTemplate = () => {
  return getRequest(`bulkImportTemplate.zip`)
}

export const requestGetSiteAttributesTemplate = () => {
  return getRequest(`${csvPath}/forms/sites`)
}

export const requestGetSiteThreatsTemplate = () => {
  return getRequest(`${csvPath}/forms/threats`)
}

export const requestGetSiteActionsTemplate = () => {
  return getRequest(`${csvPath}/forms/actions`)
}

// Add deleteCriterionC here
export const deleteCriterionC = (siteId, siteHistoryId) => {
  const url = siteHistoryId 
    ? `${reassessmentPath}/${siteHistoryId}/criterionC` 
    : `${path}/${siteId}/criterionC`;
  return deleteRequest(url);
};

export const requestEditSite = (siteId) => {
  return postRequest(`${editSitePath}/${siteId}/edit`)
}

export const requestConfirmEditOnlyState = (siteId, siteHistoryId, isApproved) => {
  const url = siteHistoryId 
    ? `${editSitePath}/${siteHistoryId}/confirm` 
    : `${editSitePath}/${siteId}/confirm`;

  return postRequest(url, { isApproved });
};

export const requestFinishEditOnlySite = (siteHistoryId) => {
  const url = `${editSitePath}/${siteHistoryId}/finishEditOnlyEdits` 

  return postRequest(url);
};

export const requestUpdateEditOnlySite = (siteId, data) => {
  return patchRequest(`${editSitePath}/${siteId}`, data);
};

export const requestUpdateSpeciesFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/species-files`
    : `${siteSpeciePath}/${siteId}/species-files`
  return xhrFormDataRequest({
    url,
    method: siteHistoryId ? 'PATCH' : 'POST',
    formData,
    onProgress: options.onProgress
  })
}


// --- Corrected requestDeleteSpeciesFiles function ---
export const requestDeleteSpeciesFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId
    // If siteHistoryId exists, use reassessment path and append fileId to 'species-files'
    ? `${reassessmentPath}/${siteHistoryId}/species-files/${fileId}`
    // Otherwise, use the base path and append fileId to 'species-files'
    : `${siteSpeciePath}/${siteId}/species-files/${fileId}` // Corrected path: removed 'deleteSpeciesFiles' and added fileId to 'species-files'

  const { data } = await fetchJsonRequest(url, {
    method: 'DELETE',
    fallback: {}
  })
  return data
}

// api/site.js (or wherever requestGetSpeciesFiles lives)
export const requestGetSpeciesFiles = async (siteId, siteHistoryId) => {
  // Handle both object and primitive formats for backward compatibility
  let actualSiteId = siteId
  let actualSiteHistoryId = siteHistoryId

  // If first param is an object, extract values from it
  if (siteId && typeof siteId === 'object' && !Array.isArray(siteId)) {
    actualSiteId = siteId.id || siteId.siteId || siteId.SiteId
    actualSiteHistoryId = siteId.siteHistoryId || siteId.sitehistoryId || siteId.historyId || siteHistoryId
  }

  if (!actualSiteId) {
    throw new Error("requestGetSpeciesFiles: siteId is required");
  }

  // Ensure siteId is a primitive value
  const primitiveSiteId = typeof actualSiteId === 'object' ? (actualSiteId?.id || actualSiteId?.siteId) : actualSiteId
  const primitiveHistoryId = actualSiteHistoryId && typeof actualSiteHistoryId === 'object' 
    ? (actualSiteHistoryId?.siteHistoryId || actualSiteHistoryId?.historyId) 
    : actualSiteHistoryId

  const qs = primitiveHistoryId ? `?siteHistoryId=${encodeURIComponent(primitiveHistoryId)}` : "";
  const url = `${siteSpeciePath}/${primitiveSiteId}/species-files${qs}`;

  try {
    const { response, data } = await fetchJsonRequest(url, {
      method: 'GET',
      fallback: { files: [] },
      onParseError: (error) => console.warn('[API][species-files][GET:json-parse-failed]', error)
    })

    if (response.status === 404) {
      return { files: [] };
    }

    if (!response.ok) {
      return { files: [] };
    }

    return data ?? { files: [] };
  } catch (error) {
    console.warn("[API][species-files][GET:error]", error);
    return { files: [] };
  }
};

// Criterion C files API functions
export const requestUpdateCriterionCFiles = async (siteId, siteHistoryId, formData, options = {}) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/criterion-c-files`
    : `${criterionCPath}/${siteId}/criterion-c-files`
  return xhrFormDataRequest({
    url,
    method: siteHistoryId ? 'PATCH' : 'POST',
    formData,
    onProgress: options.onProgress
  })
}

export const requestDeleteCriterionCFiles = async (siteId, siteHistoryId, fileId) => {
  const url = siteHistoryId
    ? `${reassessmentPath}/${siteHistoryId}/criterion-c-files/${fileId}`
    : `${criterionCPath}/${siteId}/criterion-c-files/${fileId}`

  const { response, data } = await fetchJsonRequest(url, {
    method: 'DELETE',
    credentials: 'include',
    fallback: {}
  })

  if (!response.ok) {
    throw new Error(`Failed to delete criterion C file (${response.status})`)
  }

  return data
}

export const requestGetCriterionCFiles = async (siteId, siteHistoryId) => {
  if (!siteId) {
    throw new Error("requestGetCriterionCFiles: siteId is required");
  }

  const qs = siteHistoryId ? `?siteHistoryId=${encodeURIComponent(siteHistoryId)}` : "";
  const url = `${criterionCPath}/${siteId}/criterion-c-files${qs}`;

  const { response, data } = await fetchJsonRequest(url, {
    method: 'GET',
    credentials: 'include',
    fallback: { files: [] },
    onParseError: (error) => console.warn('[API][criterion-c-files][GET:json-parse-failed]', error)
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch criterion C files (${response.status})`);
  }

  return data ?? { files: [] };
};

// Get site files (for about page)
export const requestGetSiteFiles = async (siteId, siteHistoryId) => {
  if (!siteId) {
    throw new Error("requestGetSiteFiles: siteId is required");
  }

  const qs = siteHistoryId ? `?siteId=${encodeURIComponent(siteId)}` : "";
  const url = siteHistoryId 
    ? `${reassessmentPath}/${siteHistoryId}/files${qs}`
    : `/api/site/${siteId}/files${qs}`;

  const { response, data } = await fetchJsonRequest(url, {
    method: 'GET',
    fallback: { files: [] },
    onParseError: (error) => console.warn('[API][site-files][GET:json-parse-failed]', error)
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch site files (${response.status})`);
  }

  return data ?? { files: [] };
};




